import { useState } from "react";
import { Card } from "@/components/ui/card";

interface BundleOption {
  quantity: number;
  price: number;
  originalPrice: number;
  discount: number;
  label: string;
  isPopular?: boolean;
  badge?: string;
}

interface BundlePreviewProps {
  settings: {
    title?: string;
    subtitle?: string;
    bundles?: BundleOption[];
    currency?: string;
    buttonText?: string;
    selectedBundleIndex?: number;
    backgroundColor?: string;
    borderColor?: string;
    selectedBorderColor?: string;
    textColor?: string;
    priceColor?: string;
    originalPriceColor?: string;
    discountColor?: string;
    buttonColor?: string;
    buttonTextColor?: string;
    badgeColor?: string;
    badgeTextColor?: string;
    popularBadgeColor?: string;
    borderRadius?: string;
    padding?: string;
    showBadge?: boolean;
    linkToForm?: boolean;
  };
}

export const BundlePreview = ({ settings }: BundlePreviewProps) => {
  const [selectedIndex, setSelectedIndex] = useState(settings.selectedBundleIndex || 0);

  const bundles = settings.bundles || [];

  return (
    <div 
      className="w-full max-w-2xl mx-auto px-4 py-6"
      style={{
        backgroundColor: settings.backgroundColor || "#ffffff",
      }}
    >
      {/* Title */}
      {settings.title && (
        <h2 
          className="text-xl md:text-2xl font-bold text-center mb-2"
          style={{ color: settings.textColor || "#000000" }}
        >
          {settings.title}
        </h2>
      )}

      {/* Subtitle */}
      {settings.subtitle && (
        <p 
          className="text-sm md:text-base text-center mb-6"
          style={{ color: settings.textColor || "#000000", opacity: 0.7 }}
        >
          {settings.subtitle}
        </p>
      )}

      {/* Bundle Options */}
      <div className="space-y-3 md:space-y-4">
        {bundles.map((bundle, index) => (
          <div
            key={index}
            className="relative cursor-pointer transition-all"
            onClick={() => setSelectedIndex(index)}
          >
            {/* Badge */}
            {settings.showBadge && bundle.badge && (
              <div 
                className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-3 py-1 text-xs font-bold rounded-full z-10 shadow-sm"
                style={{
                  backgroundColor: bundle.isPopular 
                    ? settings.popularBadgeColor || "#10b981"
                    : settings.badgeColor || "#17C3CE",
                  color: settings.badgeTextColor || "#ffffff",
                }}
              >
                {bundle.badge}
              </div>
            )}

            <Card
              className="transition-all hover:shadow-lg"
              style={{
                borderWidth: "3px",
                borderColor: selectedIndex === index 
                  ? settings.selectedBorderColor || "#17C3CE" 
                  : settings.borderColor || "#e5e7eb",
                borderRadius: `${settings.borderRadius || 12}px`,
                padding: "16px",
                marginTop: settings.showBadge && bundle.badge ? "8px" : "0",
              }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                {/* Radio Button and Label */}
                <div className="flex items-center gap-3 flex-1">
                  <div 
                    className="w-6 h-6 rounded-full border-3 flex items-center justify-center transition-all flex-shrink-0"
                    style={{
                      borderWidth: "3px",
                      borderColor: selectedIndex === index 
                        ? settings.selectedBorderColor || "#17C3CE"
                        : "#d1d5db",
                      backgroundColor: selectedIndex === index 
                        ? settings.selectedBorderColor || "#17C3CE"
                        : "transparent",
                    }}
                  >
                    {selectedIndex === index && (
                      <div className="w-3 h-3 rounded-full bg-white" />
                    )}
                  </div>

                  {/* Label */}
                  <div className="flex-1">
                    <p 
                      className="font-bold text-base md:text-lg"
                      style={{ color: settings.textColor || "#000000" }}
                    >
                      {bundle.label}
                    </p>
                    {bundle.discount > 0 && (
                      <p 
                        className="text-xs md:text-sm font-medium mt-0.5"
                        style={{ color: settings.discountColor || settings.selectedBorderColor || "#17C3CE" }}
                      >
                        وفر {bundle.discount}%
                      </p>
                    )}
                  </div>
                </div>

                {/* Prices */}
                <div className="flex items-center gap-2 md:gap-3 justify-between md:justify-end">
                  <div className="text-right">
                    <div 
                      className="text-xl md:text-2xl font-bold"
                      style={{ color: settings.priceColor || "#000000" }}
                    >
                      {settings.currency || "EGP"}{bundle.price.toFixed(2)}
                    </div>
                    <div 
                      className="text-xs md:text-sm line-through opacity-60"
                      style={{ color: settings.originalPriceColor || "#999999" }}
                    >
                      {settings.currency || "EGP"}{bundle.originalPrice.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>

      {/* Button */}
      {settings.buttonText && (
        <div className="mt-6">
          <button
            className="w-full py-3 md:py-4 px-6 rounded-lg font-bold text-base md:text-lg transition-all hover:opacity-90 shadow-md"
            style={{
              backgroundColor: settings.buttonColor || settings.selectedBorderColor || "#17C3CE",
              color: settings.buttonTextColor || "#ffffff",
            }}
          >
            {settings.buttonText}
          </button>
        </div>
      )}
    </div>
  );
};
